import React, { FC, useState, useEffect } from 'react';
import Alert from 'react-bootstrap/Alert';
import AppAvatar from './AppAvatar';

export interface NotificationData {
  level: 'danger' | 'warning' | 'info' | 'success';
  message: string;
  dismissible?: boolean; // Allow dismissible to be set per notification
  timeout?: number;
  onClick?: () => void; // Optional click handler for the notification
  avatarUrl?: string; // Optional avatar URL to display in the notification
}

const VALID_LEVELS: NotificationData['level'][] = ['danger', 'warning', 'info', 'success'];

// PHP flashes historically used 'error', which is not a react-bootstrap Alert variant
export const normalizeNotificationLevel = (level: string): NotificationData['level'] => {
  if (level === 'error') {
    return 'danger';
  }
  return VALID_LEVELS.includes(level as NotificationData['level'])
    ? (level as NotificationData['level'])
    : 'info';
};

interface NotificationProps {
  notifications: NotificationData[];
  onDismiss: (index: number) => void;
  className?: string; // Added className prop
  defaultTimeout?: number; // Default timeout for notifications
}

const Notification: FC<NotificationProps> = ({ notifications, onDismiss, className, defaultTimeout = 3000 }) => {
  const [exitingIndexes, setExitingIndexes] = useState<Set<number>>(new Set());

  // Dynamically compute nav height for overlay variant to avoid layout shift
  useEffect(() => {
    let lastOffset = -1;
    let ticking = false;

    const computeOffset = () => {
      const navs = Array.from(document.querySelectorAll<HTMLElement>('.primary-nav'));
      if (!navs.length) return 0;
      const bottoms: number[] = [];
      navs.forEach(el => {
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') return;
        const rect = el.getBoundingClientRect();
        // Only consider navbars starting near the top portion of viewport (avoid off-screen duplicates)
        if (rect.height > 0 && rect.top < window.innerHeight * 0.4) {
          // If partially scrolled out, rect.bottom naturally reflects remaining visible extent
            bottoms.push(Math.max(0, rect.bottom));
        }
      });
      if (!bottoms.length) return 0;
      return Math.round(Math.max(...bottoms));
    };

    const setOffset = () => {
      const offset = computeOffset();
      if (offset !== lastOffset) {
        lastOffset = offset;
        document.documentElement.style.setProperty('--nav-offset', offset + 'px');
      }
      ticking = false;
    };

    const requestUpdate = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(setOffset);
      }
    };

    // Initial
    requestUpdate();
    // Events that can change nav height/visibility
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    window.addEventListener('load', requestUpdate);

    // Fallback periodic check (in case of dynamic layout shifts / font load) for first few seconds
    const interval = window.setInterval(requestUpdate, 1500);
    const timeout = window.setTimeout(() => clearInterval(interval), 6000);

    return () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      window.removeEventListener('load', requestUpdate);
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  const handleDismiss = (index: number) => {
    // Add to exiting set for animation
    setExitingIndexes(prev => new Set(prev).add(index));
    
    // Wait for animation to complete before actually removing
    setTimeout(() => {
      onDismiss(index);
      setExitingIndexes(prev => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    }, 300); // Match the animation duration
  };

  useEffect(() => {
    // Set up a timer for each notification
    const timers = notifications.map((notification, index) => {
      // Only set timer if dismissible is not false
      if (notification.dismissible !== false) {
        const timeout = notification.timeout ?? defaultTimeout;
        return setTimeout(() => {
          handleDismiss(index);
        }, timeout);
      }
      return null;
    });
    // Cleanup timers on unmount or notifications change
    return () => {
      timers.forEach(timer => timer && clearTimeout(timer));
    };
  }, [notifications]);
  
  return (
    <div className={className}> {/* container */}
      <div className="global-notifications__inner">
        {notifications.map((notification, index) => (
          <Alert
            key={index}
            className={`p-0 ${exitingIndexes.has(index) ? 'alert-exit' : ''} ${notification.onClick ? 'clickable' : ''}`}
            variant={normalizeNotificationLevel(notification.level)}
            onClose={() => handleDismiss(index)}
            dismissible={notification.dismissible !== false}
            onClick={(e) => {
              if (notification.onClick && !(e.target as HTMLElement).closest('.btn-close')) {
                notification.onClick();
                handleDismiss(index);
              }
            }}
          >
            <div className={`notification-content ${notification.avatarUrl ? 'with-avatar' : ''}`}>
              {notification.avatarUrl && (
                <AppAvatar
                  src={notification.avatarUrl}
                  circle
                  size="sm"
                  className="notification-avatar"
                />
              )}
              <span className="notification-message">{notification.message}</span>
            </div>
          </Alert>
        ))}
      </div>
    </div>
  );
};

export default Notification;